import { Text } from '@afterpaytouch/core'
import { PCL_CARD_EXPIRY_LIMIT_IN_MONTHS } from '../../utils/credit-card'
import React from 'react'
import { CashAppPayDetail, PaymentMethodType, PaymentType } from '@afterpay/types'
import { useTranslation } from '../../utils/convergence'
import styles from './style.module.css'
import { EnrichedBankAccountDetails, EnrichedCardDetails, Features } from '../../model'
import { useIsAfterpayFinanceFeeEligible, useSelectedPaymentType } from '../../state'
import { useFlag } from '@afterpaytouch/feature'
import { getPaymentMethodCardErrorMessage } from '../../utils/translation-helpers'

interface PaymentMethodErrorMessageProps {
  paymentMethodType: PaymentMethodType
  card?: EnrichedCardDetails
  account?: EnrichedBankAccountDetails
  cashAppPay?: CashAppPayDetail
}

const cardExpiryLimitInMonths = PCL_CARD_EXPIRY_LIMIT_IN_MONTHS

export const PaymentMethodErrorMessage: React.FC<PaymentMethodErrorMessageProps> = ({ paymentMethodType, card, account, cashAppPay }) => {
  const { t } = useTranslation()
  const isAff = useIsAfterpayFinanceFeeEligible()

  const selectedPaymentType = useSelectedPaymentType() ?? PaymentType.PBI
  const cashPayMonthlyEnabled = useFlag(Features.CONVERGENCE_CASH_PAY_MONTHLY_ENABLED)

  const getErrorMessage = (): string | undefined => {
    if (paymentMethodType === PaymentMethodType.ACH_BANK_ACCOUNT && account?.error) {
      return t(`paymentMethod:bankAccountErrors.generic`)
    }

    if (paymentMethodType === PaymentMethodType.CASH_APP_PAY && cashAppPay != null) {
      const isConsumerLending = selectedPaymentType === PaymentType.PCL
      const isInactive = cashAppPay.status !== 'ACTIVE'
      const hasError = (isConsumerLending && !cashPayMonthlyEnabled) || isInactive

      if (hasError) {
        return t(`paymentMethod:errors.cashAppPayNotAllowed_inline`)
      }
    }

    if (paymentMethodType === PaymentMethodType.CREDIT_CARD && card?.error) {
      return getPaymentMethodCardErrorMessage({
        t,
        cardError: card.error,
        cardBrand: card.cardBrand ?? '',
        cardExpiryLimitInMonths,
        isInline: true,
      })
    }

    if (isAff) {
      if (paymentMethodType === PaymentMethodType.CREDIT_CARD && card?.creditCardType !== 'DEBIT') {
        return t('paymentMethod:errors.ineligibleCreditCard')
      }

      if (paymentMethodType === PaymentMethodType.ACH_BANK_ACCOUNT) {
        return t('paymentMethod:errors.ineligibleBankAccount')
      }
    }
  }

  const errorMessage = getErrorMessage()

  if (!errorMessage) {
    return null
  }

  return (
    <div className={styles.radioButtonErrorContainer}>
      <Text size={'XS'} color='Subtle'>
        {errorMessage}
      </Text>
    </div>
  )
}
